import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { EntityCreateComponent } from '@component/entity-create-component';
import { tinyDefaultConfig } from '@configuration/tiny-default-config';
import { EditImageDialogComponent } from '@modal/edit-image-dialog/edit-image-dialog.component';
import { ComputedImage } from '@model/computed-image';
import { Blog } from '@model/entities/blog';
import { User } from '@model/user/user';
import { BlogService } from '@service/blog.service';
import { EntityTypeConfigService } from '@service/entity-type-config.service';
import { HelperService } from '@service/helper.service';
import { LoadingService } from '@service/loading.service';
import { MobileService } from '@service/mobile.service';
import { ScrollService } from '@service/scroll.service';
import { SentryService } from '@service/sentry.service';
import { StateService } from '@service/state.service';
import { ToastService } from '@service/toast/toast.service';
import { TransitionHookService } from '@service/transition-hook.service';
import { UserService } from '@service/user/user.service';
import { EntityTypeIdentifier } from '@type/entity-type-identifier';
import { ImageKeys } from '@type/image-keys';

@Component({
  selector: 'app-blog-create',
  templateUrl: './blog-create.component.html',
  styleUrls: ['./blog-create.component.scss'],
})
export class BlogCreateComponent extends EntityCreateComponent implements OnInit {
  protected entityTypeIdentifier: EntityTypeIdentifier = 'blog';

  public mostViewed: Blog[] = [];

  // images
  public fallbackImages: ComputedImage[] = [];

  // draft
  public draft: Blog;

  // temp state vars
  public isVideo: boolean = false;

  public hasCompanyProfile: boolean = false;

  public user: User = null;

  private currentDragImg: ComputedImage;
  private lastHoverElem: HTMLElement;

  public tinyAutogrowConfig = {
    ...tinyDefaultConfig,
    plugins: tinyDefaultConfig.plugins + ' autoresize',
  };

  public get shouldShowProfileSelect(): boolean {
    return this.getHasCompanyProfile && !this.userService.isPremiumCompanyProfile();
  }

  constructor(
    public userService: UserService,
    private blogService: BlogService,
    protected toastService: ToastService,
    protected transitionHookService: TransitionHookService,
    protected dialog: MatDialog,
    private sentryService: SentryService,
    protected scrollService: ScrollService,
    public entityTypeConfigService: EntityTypeConfigService,
    public Mobileservice: MobileService,
    protected LoadingService: LoadingService,
    protected HelperService: HelperService,
    public StateService: StateService,
    public ActivatedRoute: ActivatedRoute,
    public Router: Router,
    private MatDialog: MatDialog,
  ) {
    super(
      entityTypeConfigService,
      scrollService,
      userService,
      LoadingService,
      HelperService,
      transitionHookService,
      dialog,
      toastService,
      StateService,
      ActivatedRoute,
      Router,
    );

    this.userService.userObservable.subscribe((user) => {
      if (user) {
        this.user = user;

        // check company profile existence to whether skip the profile selector step or not
        this.checkCompanyProfile();
      }
    });
  }

  protected initFormGroup(): void {
    // If the user has a premium company profile we don't display the profile selector,
    // because they can only post via their company profile.
    // However, we then also need to mark the first step as completed.
    // Otherwise the stepper prevents us fimmediately rom going to the third step.
    // See https://gitea.aceart.de/aceArtGmbH/zimmerer-treffpunkt-app/issues/347 for more information.
    if (!this.shouldShowProfileSelect) {
      this.stepsCompleted = Math.max(this.stepsCompleted, 1);
    }

    super.initFormGroup();
  }

  public ngOnInit() {
    super.ngOnInit();

    // get default fallback images
    this.blogService.getStockImages().then((stockimages) => {
      this.fallbackImages = stockimages;
    });

    this.blogService
      .getMostViewed()
      .then((mostViewed) => {
        this.mostViewed = mostViewed;
      })
      .catch((err) => {
        this.sentryService.silentCaptureException(err);
      });
  }

  drop(event: DragEvent, targetImage: any) {
    event.preventDefault();

    let sourceIndex = this.draft.images.findIndex((img) => img.image.id === this.currentDragImg.image.id);
    let targetIndex = this.draft.images.findIndex((img) => img.image.id === targetImage.image.id);

    if (sourceIndex === targetIndex) return;

    let movedImage = this.draft.images[sourceIndex];

    this.draft.images.splice(sourceIndex, 1);

    if (sourceIndex < targetIndex) {
      targetIndex -= 1;
    }

    this.draft.images.splice(targetIndex, 0, movedImage);

    this.draft.images.forEach((img, index) => {
      img.image.order = index;
    });

    this.lastHoverElem.classList.remove('drag-preview');
    this.updateDraft();
  }

  allowDrop(event: Event, image) {
    this.lastHoverElem.classList.remove('drag-preview');
    this.lastHoverElem = (event.target as HTMLElement).closest('.dropbox');
    this.lastHoverElem.classList.add('drag-preview');
    event.preventDefault();
  }
  drag(event: DragEvent, image: ComputedImage) {
    this.currentDragImg = image;
    this.lastHoverElem = (event.target as HTMLElement).closest('.dropbox');
  }

  public deleteImage(imageId: number, key: ImageKeys): Promise<void> {
    return new Promise((resolve, reject) => {
      this.LoadingService.startLoad();

      this.blogService
        .deleteImage(this.draft.id, imageId)
        .then((draft: Blog) => {
          this.draft = draft;
          this.initFormGroup();

          this.LoadingService.stopLoad();
          resolve();
        })
        .catch((err) => {
          this.LoadingService.stopLoad();
          reject(err);
        });
    });
  }
  public editImage(imageId: number, key: ImageKeys): Promise<void> {
    let imageIndex = this.draft.images.findIndex((image) => image.image.id === imageId);

    if (imageIndex == 0 && this.draft.images[imageIndex].purpose_key == 'MAIN') imageIndex = 1;

    return new Promise((resolve, reject) => {
      const dialogRef = this.MatDialog.open(EditImageDialogComponent, {
        data: { description: this.draft.images[imageIndex].image.description },
        panelClass: 'black-centered-dialog',
        width: '40vw',
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.draft.images[imageIndex].image.description = result.description;

          // Because MAIN Image is in the first both entrys of images
          // but only the second one gets shown in the slider
          if (imageIndex == 1 && this.draft.images[0].purpose_key == 'MAIN')
            this.draft.images[0].image.description = result.description;
          this.updateDraft();
        }
      });
    });
  }

  public showFallbackImages() {
    // don´t show if there is a legit user uploaded main image
    if (this.draft.mainImage && !this.draft.mainImage.isFallback) {
      return false;
    }

    // never show fallback image for videos
    if (this.isVideo) {
      return false;
    }

    // if there is a main image and it is already a fallback image -> show fallback options
    if (this.draft.mainImage && this.draft.mainImage.isFallback) {
      return true;
    }

    // if user tried to submit
    if (this.formSubmitted)
      if (this.draft.mainImage ? !this.draft.mainImage.isFallback : true)
        // and there is no non-fallback image -> show them
        return true;

    // default dont show them
    return false;
  }

  public setFallbackImage(image: ComputedImage) {
    this.form.patchValue({
      fallbackImage: image,
    });
    this.draft.fallbackImage = image;
    this.updateDraft();
  }

  /*
   *   sure one could imagine this.form.value.category.name==='videos' in template
   *   (and not extra manually calculating it each time it updates)
   *   but I decided for it for performance reasons
   *   I once logged the count how often calculation would be done in template
   *   browser tab crashed because the log was to big
   */
  protected calcStateVars() {
    // TODO find a better way to check category name
    this.isVideo =
      this.form != null && this.form.value.category && this.form.value.category.name.toLowerCase().includes('video');
  }

  /*
  *   @override

  *   This method is executed every time the form group gets initiated and its an edit
  */
  public customEditActions() {
    this.form.get('profile_type').disable();
  }

  public checkCompanyProfile() {
    if (this.userService.hasCompanyProfile()) {
      this.hasCompanyProfile = true;
    } else {
      this.stepsCompleted++;
    }
  }

  public get getHasCompanyProfile() {
    return this.hasCompanyProfile;
  }

  public get getHasEnoughtCompanyProfileCredits() {
    if (this.user.company_profile.credits < this.appConfig.credits['BLOG']) {
      return true;
    }

    return false;
  }
}
